import * as React from 'react'
import { Link } from 'react-router-dom'
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from 'lucide-react'
import { useDeleteOwnOffering, useReorderOwnOfferings } from '@/hooks/useVendors'
import { requestErrorMessage } from '@/helpers/apiClient'
import { OfferingDialog } from '@/components/vendor-profile/OfferingDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { FormError } from '@/components/ui/form-error'
import { VENDOR_SERVICES, VENDOR_SERVICE_LABELS } from '@lib/enums/vendor-service.enum'
import { VENDOR_OFFERING_KIND_LABELS } from '@lib/enums/vendor-offering-kind.enum'
import { isOfferedService, type OfferedService } from '@lib/input/vendor-offering.input'
import { offeringPriceLabel } from '@lib/helpers/vendor-offering'
import { MAX_VENDOR_OFFERINGS } from '@lib/constants/vendor-profile'
import type { OwnVendorOfferingDto, OwnVendorProfileDto } from '@lib/dto/vendor-profile.dto'

const iconButton =
  'grid h-7 w-7 place-items-center rounded text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink disabled:opacity-40'

interface Editing {
  service: OfferedService
  offering: OwnVendorOfferingDto | null
}

/**
 * What the company offers, one section per service it offers (furniture
 * aside, which keeps its catalog): packages and items with their prices or
 * "price on request". Packages for a service it has stopped offering are kept,
 * shown last, and seen by nobody else.
 */
export function OfferingsCard({ profile }: { profile: OwnVendorProfileDto }) {
  const remove = useDeleteOwnOffering()
  const reorder = useReorderOwnOfferings()
  const [editing, setEditing] = React.useState<Editing | null>(null)
  const [removing, setRemoving] = React.useState<OwnVendorOfferingDto | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const offered = VENDOR_SERVICES.filter((service): service is OfferedService => isOfferedService(service) && profile.services.includes(service))
  const offersFurniture = profile.services.includes('furniture')
  const dropped = profile.offerings.filter((offering) => !profile.services.includes(offering.service))
  const full = profile.offerings.length >= MAX_VENDOR_OFFERINGS

  /** Swap an offering with its neighbor in its own service's list, keeping everyone else's place. */
  function move(offering: OwnVendorOfferingDto, by: -1 | 1): void {
    const ids = profile.offerings.map((o) => o.id)
    const group = profile.offerings.filter((o) => o.service === offering.service)
    const neighbor = group[group.findIndex((o) => o.id === offering.id) + by]
    if (neighbor === undefined) return
    const a = ids.indexOf(offering.id)
    const b = ids.indexOf(neighbor.id)
    ids[a] = neighbor.id
    ids[b] = offering.id
    setError(null)
    reorder.mutate({ offeringIds: ids }, { onError: (e) => setError(requestErrorMessage(e)) })
  }

  function rows(list: readonly OwnVendorOfferingDto[], canAdd: boolean) {
    return (
      <ul className="m-0 list-none p-0">
        {list.map((offering, index) => (
          <li key={offering.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line py-2.5 last:border-b-0">
            <span className="min-w-0 flex-1">
              <span className="font-medium text-ink">{offering.name}</span>
              <span className="ml-2 align-middle">
                <Badge>{VENDOR_OFFERING_KIND_LABELS[offering.kind]}</Badge>
              </span>
              <span className="block text-sm text-ink-muted">{offeringPriceLabel(offering.price)}</span>
            </span>
            {canAdd ? (
              <>
                <button type="button" className={iconButton} aria-label={`Move ${offering.name} up`} disabled={index === 0 || reorder.isPending} onClick={() => move(offering, -1)}>
                  <ArrowUp className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  className={iconButton}
                  aria-label={`Move ${offering.name} down`}
                  disabled={index === list.length - 1 || reorder.isPending}
                  onClick={() => move(offering, 1)}
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </button>
                <Button variant="ghost" size="sm" onClick={() => setEditing({ service: offering.service, offering })}>
                  <Pencil className="h-4 w-4" /> Edit
                </Button>
              </>
            ) : null}
            <Button variant="ghost" size="sm" aria-label={`Remove ${offering.name}`} onClick={() => setRemoving(offering)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </li>
        ))}
      </ul>
    )
  }

  return (
    <Card id="offerings">
      <CardHeader>
        <CardTitle>What you offer</CardTitle>
        <CardDescription>Packages and items, each with its price or “price on request”, and photos.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {offered.length === 0 && !offersFurniture ? (
          <p className="text-sm text-ink-muted">
            Choose what you offer in{' '}
            <Link to="/vendor" className="underline underline-offset-2">
              Settings
            </Link>{' '}
            first.
          </p>
        ) : null}

        {offered.map((service) => {
          const list = profile.offerings.filter((offering) => offering.service === service)
          return (
            <section key={service} className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="m-0 text-sm font-semibold text-ink">{VENDOR_SERVICE_LABELS[service]}</h3>
                <Button variant="outline" size="sm" disabled={full} onClick={() => setEditing({ service, offering: null })}>
                  <Plus className="h-4 w-4" /> Add a package or item
                </Button>
              </div>
              {list.length === 0 ? <p className="text-sm text-ink-muted">Nothing listed yet.</p> : rows(list, true)}
            </section>
          )
        })}

        {offersFurniture ? (
          <p className="text-sm text-ink-muted">
            Your furniture is in your{' '}
            <Link to="/vendor/furniture" className="underline underline-offset-2">
              Furniture
            </Link>{' '}
            catalog, priced per piece.
          </p>
        ) : null}

        {dropped.length > 0 ? (
          <section className="space-y-2">
            <h3 className="m-0 text-sm font-semibold text-ink">No longer offered</h3>
            <p className="m-0 text-sm text-ink-muted">
              Nobody else sees these. Add the service back in Settings, or remove them.
            </p>
            {rows(dropped, false)}
          </section>
        ) : null}

        {full ? <p className="text-sm text-ink-muted">That’s {String(MAX_VENDOR_OFFERINGS)}, the most a profile lists.</p> : null}
        {error ? <FormError>{error}</FormError> : null}
      </CardContent>

      {editing !== null ? (
        <OfferingDialog
          key={editing.offering?.id ?? `new-${editing.service}`}
          service={editing.service}
          offering={editing.offering}
          onClose={() => setEditing(null)}
        />
      ) : null}

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open) setRemoving(null)
        }}
        title={`Remove ${removing?.name ?? 'this package'}?`}
        description="It comes off your profile, with its photos."
        confirmLabel="Remove"
        destructive
        busy={remove.isPending}
        error={remove.error ? requestErrorMessage(remove.error) : null}
        onConfirm={() => {
          if (removing === null) return
          remove.mutate(removing.id, { onSuccess: () => setRemoving(null) })
        }}
      />
    </Card>
  )
}
